import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import SwitchTsoPq from '@components/Switch/SwitchTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { Chart, type ChartOptions } from '@highcharts/react';
import '@highcharts/react/series/BoxPlot';
import { FormGroup } from '@mui/material';
import { StyledTsoIndicator } from '@pages/MeasurementVisualization/Section/MeasurementVisualizationChart';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxisLabelsFormatterContextObject, Point } from 'highcharts/highcharts.src';
import { useFilteredBoxplotData } from './harmonicBackgroundLevelHelpers';
import type { harmonicsBoxplotProps } from './harmonicBackgroundLevelHelpers';
import {
  StyledBoxplotContainer,
  StyledBoxplotSwitchComponent,
  StyledBoxplotSwitchLabel,
  StyledBoxplotTtileLine,
  StyledBoxplotTtileSection,
  StyledBoxplotWrapper,
} from './harmonicBackgroundLevelStyles';

export const HarmonicBackgroundLevelBoxplot = ({
  plotBands,
  adaptedData,
  categories,
  tsoId,
  measurementDeviceLabel,
}: harmonicsBoxplotProps) => {
  const { t } = useTranslation();
  const [showOnlyWorstPhase, setShowOnlyWorstPhase] = useState(false);

  const { filteredData, labels, tickPositions } = useFilteredBoxplotData(adaptedData, categories, showOnlyWorstPhase);

  const options: ChartOptions = {
    chart: {
      type: 'boxplot',
      backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
      plotBackgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
      width: 1180,
      reflow: true,
      scrollablePlotArea: {
        minWidth: 1180,
        scrollPositionX: 0,
      },
    },
    title: { text: '' },
    xAxis: {
      type: 'linear',
      title: { text: t('harmonicsBackgroundLevel.xAxisDescription') },
      plotBands,
      tickPositions,
      labels: {
        formatter: function (this: AxisLabelsFormatterContextObject) {
          const value = this.value as number;
          return labels[value] || '';
        },
      },
    },
    yAxis: { title: { text: t('harmonicsBackgroundLevel.yAxisDescription') } },
    series: (adaptedData.length > 0
      ? [
          {
            type: 'boxplot',
            name: 'Harmonic Values',
            showInLegend: false,

            data: filteredData,
          },
          {
            type: 'scatter',
            name: 'Phase 1',
            marker: {
              radius: 6,
              symbol: 'square',
              fillColor: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled_lt,
            },
            data: [],
            showInLegend: true,
            enableMouseTracking: false,
          },
          {
            type: 'scatter',
            name: 'Phase 2',
            marker: {
              radius: 6,
              symbol: 'square',
              fillColor: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_hover_lt,
            },
            data: [],
            showInLegend: true,
            enableMouseTracking: false,
          },
          {
            type: 'scatter',
            name: 'Phase 3',
            marker: {
              radius: 6,
              symbol: 'square',
              fillColor: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed_lt,
            },
            data: [],
            showInLegend: true,
            enableMouseTracking: false,
          },
        ]
      : []) as ChartOptions['series'],
    plotOptions: { boxplot: { grouping: true, pointPadding: 0.005, groupPadding: 0.2 } },
    tooltip: {
      formatter: function () {
        const point = this as unknown as Point & {
          order: string;
          phase: number;
          low: number;
          q1: number;
          median: number;
          q3: number;
          high: number;
        };

        return `
       <b>${t('harmonicsBackgroundLevel.harmonicOrder')} ${point.order} - Phase ${point.phase}</b><br/>
       Max: ${Number(point.high).toFixed(2)}%<br/>
       95%-Quantil: ${Number(point.q3).toFixed(2)}%<br/>
       Median: ${Number(point.median).toFixed(2)}%<br/>
       5%-Quantil: ${Number(point.q1).toFixed(2)}%<br/>
       Min: ${Number(point.low).toFixed(2)}%
      `;
      },
      useHTML: true,
      backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled,
      style: {
        color: tsoPqTheme.palette.customTextColor.color_font_ft_white,
        fontSize: tsoPqTheme.typography.fontsize_body_body_small,
        fontFamily: tsoPqTheme.typography.openSans,
        lineHeight: '1.25rem',
      },
    },
    legend: {
      events: {
        itemClick: function () {
          return false;
        },
      },
      itemStyle: {
        cursor: 'default',
      },
      itemHoverStyle: {
        color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
      },
    },

    credits: { enabled: false },
  };

  return (
    <StyledBoxplotWrapper>
      <StyledBoxplotTtileLine>
        <StyledBoxplotTtileSection>
          <StyledTsoIndicator tsoId={tsoId} />
          <TypographyTsoPq typographyType="sectionTitleSMBold">{measurementDeviceLabel}</TypographyTsoPq>
        </StyledBoxplotTtileSection>
        <StyledBoxplotSwitchComponent>
          <FormGroup>
            <StyledBoxplotSwitchLabel
              control={
                <SwitchTsoPq
                  checked={showOnlyWorstPhase}
                  onChange={() => setShowOnlyWorstPhase(!showOnlyWorstPhase)}
                  switchLabel={''}
                />
              }
              label={
                <TypographyTsoPq typographyType="labelMD">
                  {t('harmonicsBackgroundLevel.harmonicSwitchLabel')}
                </TypographyTsoPq>
              }
            />
          </FormGroup>
        </StyledBoxplotSwitchComponent>
      </StyledBoxplotTtileLine>
      <StyledBoxplotContainer>
        {adaptedData.length > 0 ? (
          <Chart options={options} />
        ) : (
          <TypographyTsoPq typographyType="labelMD" style={{ textAlign: 'center', padding: '2rem' }}>
            {t('harmonicsBackgroundLevel.noDataFound')}
          </TypographyTsoPq>
        )}
      </StyledBoxplotContainer>
    </StyledBoxplotWrapper>
  );
};
